import { STUDENT_LIMITS } from '../api/students';

/**
 * PRT-01-FR-03 — the copy of the family landing page.
 *
 * The same arrangement as `teacher-landing.ts`, for the same reason: this is
 * product-managed acquisition content, "not transactional truth", and it
 * changes on a marketing cadence. The screen renders what is here and owns
 * only layout and colour — and the routes its calls to action reach, which are
 * not copy (PRT-BR-P01-11: marketing must never stand between a visitor and
 * the catalogue).
 *
 * The text itself is carried over unchanged, including its language; whether
 * the landing page should be in Spanish like the rest of the product is a
 * separate decision.
 */

/** A run of text, optionally emphasised in one of the hero's two highlight colours. */
export interface LeadSegment {
  text: string;
  emphasis?: 'accent' | 'error';
}

export interface Feature {
  emoji: string;
  label: string;
}

export interface ValueCard {
  emoji: string;
  title: string;
  body: string;
}

export interface HomePageContent {
  hero: {
    /** One entry per line of the heading. */
    titleLines: string[];
    lead: LeadSegment[];
    checkmarks: string[];
    ctaLabel: string;
  };
  banner: {
    text: string;
    ctaLabel: string;
  };
  features: Feature[];
  value: {
    title: string;
    body: string;
    cards: ValueCard[];
  };
}

/**
 * The age range the landing page advertises.
 *
 * Derived, never typed: it read "Children ages 3-18" while registration
 * refused anyone outside 6-14, so a family could be promised a place for a
 * child the next screen would turn away. It now says whatever the add-a-learner
 * form enforces, which is the server's own limit (DEC-S2-S02-03).
 */
export const audienceAgeLabel = (): string =>
  `Children ages ${STUDENT_LIMITS.minAge}-${STUDENT_LIMITS.maxAge}`;

export const homePageContent: HomePageContent = {
  hero: {
    titleLines: ['A Successful Child', 'Starts with strong', 'Foundations!'],
    lead: [
      { text: '🏆 Give your child the ' },
      { text: 'best start', emphasis: 'accent' },
      { text: ' by ensuring they master ' },
      { text: 'core and essential skills', emphasis: 'error' },
      { text: ' that set them up for lifelong success. At ' },
      { text: 'IQMaxx', emphasis: 'accent' },
      { text: ', we provide engaging, high-quality learning experiences that help children:' },
    ],
    checkmarks: [
      'Build a strong academic foundation with expert-led tutoring.',
      'Develop essential life & success skills like critical thinking and problem-solving.',
      'Explore creativity & culture through arts, music, and language.',
      'Grow with confidence in a supportive and enriching environment.',
    ],
    ctaLabel: 'Explore our Catalog! 📚',
  },
  banner: {
    text: 'Set your child on the path to success — start learning with IQMaxx today! 📚',
    ctaLabel: 'See our Courses 📚',
  },
  features: [
    { emoji: '📦', label: 'Interactive Live Classes' },
    { emoji: '🏆', label: 'Expert Instructors' },
    { emoji: '📅', label: 'Flexible Learning Options' },
    { emoji: '⏳', label: audienceAgeLabel() },
  ],
  value: {
    title: "IQMaxx: Learning That Fits Your Child's Needs! 🚀",
    body: "At IQMaxx, we believe that every child learns differently. That's why we give you the freedom to choose the right program and the best teacher for your child's success.",
    cards: [
      {
        emoji: '📚',
        title: 'Choose Your Program',
        body: 'Explore academic subjects and life skills.',
      },
      {
        emoji: '👩‍🏫',
        title: 'Find the Perfect Teacher',
        body: 'Connect with inspiring experts.',
      },
      {
        emoji: '🤸',
        title: 'Learn on your Schedule',
        body: 'Personalised and self-managed learning.',
      },
    ],
  },
};
